// External globals
import { App } from '@/services/app-compat';
import { api } from '@/services/api-class';
import { Codes } from '@/constants/codes';
import { AccessAuthorities } from '@/models';
import moment from 'moment';
import { hasValue } from '@/utils/legacy-utils';

import { ModuleType } from '@/constants/codes';
import { hasNumericValue } from '@/utils/validation';
import { CallCardStatus } from '@/services/api-types';
import { load } from '@/services/loading';
import { isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp, kstConfirm, kstPrompt, CallCard, Mousetrap, $, deepCopyProperties } from '@/utils/window-globals';

// Lazy window-global lookups — resolved at call time, not module load time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const loadNocSopImages = _wg('loadNocSopImages');

export let nocManagementAdmin: NocManagementAdmin;

export function ADMIN_NOCManagement() {
    nocManagementAdmin = new NocManagementAdmin();
}

export class NocManagementAdmin {
    currentNocManagementScreen: any = null;

    manageNocRows = { entries: [] };
    manageNocPager = { curPage: null, numPages: null, records: null, isVisible: false };

    selfInitiateNocRows = { entries: [] };
    selfInitiateNocPager = { curPage: null, numPages: null, records: null, isVisible: false };

    pageShowing = "showManageNoc";
    startingTab = "showManageNocTab";

    //cached values
    selfInitiateNocList = null;
    natureOfCallList = null;
    globalNatureOfCallList = null;
    groupAndSubgroupNocCodes = null;

    clearCompanyNatureOfCallCachedValues() {
        this.natureOfCallList = null;
    }

    clearSelfInitiateCachedValues() {
        this.selfInitiateNocList = null;
    }

    public constructor() {
        setTimeout(() => {
            this.mountScreen();
        }, 0);
    }

    updateInitialRowOrder(values: Array<{ companyId: number, listIdx: number, code: number, value: string, isListEnabled: boolean, isEnabled: boolean, id?: number }>) {
        sortNatureOfCallList(values);
        for (let i = 0; i < values.length; i++) {
            values[i].listIdx = i + 1;
            values[i].isListEnabled = true;
            values[i].id = values[i].code;
        }
    }

    refreshManageNocList(isInitialLoad: boolean = false, doScrollToBottom: boolean = false) {
        // The React component manages its own data refresh internally.
        // This is kept as a no-op for backward compatibility.
    }

    scrollToBottomOfNocManagementGrid() {
        // The React component manages its own grid scrolling internally.
    }

    private _getCachedPromise<T>(
        cachedValue: T | null,
        setCachedValue: (result: T) => void,
        apiCall: (success: (result: T) => void, error: (err: any) => void) => void,
        errorMessage: string,
    ): Promise<T> {
        return new Promise((resolve, reject) => {
            if (hasValue(cachedValue)) {
                resolve(deepCopyProperties(cachedValue));
            } else {
                apiCall(result => {
                    setCachedValue(result);
                    resolve(deepCopyProperties(result));
                }, errorResult => {
                    reject(errorResult);
                    App.showPopupNotification(errorMessage, 'error');
                });
            }
        });
    }

    getGlobalNatureOfCallListPromise() {
        return this._getCachedPromise(
            this.globalNatureOfCallList,
            r => { this.globalNatureOfCallList = r; },
            (s, e) => api.listGlobalNatureOfCalls(s, e),
            "Error getting standard nature of call data.",
        );
    }

    getCompanyNatureOfCallListPromise() {
        return this._getCachedPromise(
            this.natureOfCallList,
            r => { this.natureOfCallList = r; },
            (s, e) => api.listCompanyNatureOfCalls(s, e),
            "Error getting nature of call data.",
        );
    }

    getGroupAndSubgroupCodesPromise() {
        return this._getCachedPromise(
            this.groupAndSubgroupNocCodes,
            r => { this.groupAndSubgroupNocCodes = r; },
            (s, e) => api.getGroupAndSubgroupNocCodes(s, e),
            "Error getting nature of call group/subgroup data.",
        );
    }

    getSelfInitiateNocListPromise() {
        return this._getCachedPromise(
            this.selfInitiateNocList,
            r => { this.selfInitiateNocList = r; },
            (s, e) => api.getSelfInitiateNocList(s, e),
            "Error getting self initiate list.",
        );
    }

    refreshSelfInitiateList(clearSelfInitiateCache: boolean = false) {
        // The React component manages its own self-initiate refresh internally.
    }

    viewSop(natureOfCallCode) {
        // The React component handles SOP view internally via event delegation.
    }

    loadSopImagesForView(sopContent: string, natureOfCallCode: number, companyId: number, callback: (processedContent: string) => void) {
        loadNocSopImages(sopContent, natureOfCallCode, companyId, callback);
    }

    mountScreen() {
        const container = document.getElementById('NocManagementSection');
        if (container) {
            (window as any).OrnaReact.renderComponent(
                (window as any).NocManagementScreenReact,
                {},
                container
            );
        }
        this.currentNocManagementScreen = {
            destroy() {
                const c = document.getElementById('NocManagementSection');
                if (c) (window as any).OrnaReact.destroyComponent(c);
            },
        };
    }

    nocManagementColumns = [
        {
            field: "code", name: "Code", size: 'hidden'
        },
        {
            field: "value", name: "Nature of Call", size: 'col-xs-3',
            template: function (e) {
                if (hasValue(e) && hasValue(e.value)) {
                    const style = e.isEnabled === false ? 'opacity: 0.5;' : '';
                    return `<span style="${style}">${e.value}</span>`;
                } else {
                    return "Error getting Nature of Call."
                }
            },
        },
        {
            field: "gValue", name: "Group", size: 'col-xs-2',
            template: function (e) {
                if (hasValue(e) && hasValue(e.gCode)) {
                    const style = e.isEnabled === false ? 'opacity: 0.5;' : '';
                    return `<span style="${style}">${e.gValue}</span>`;
                } else {
                    return "Error getting group."
                }
            },
        }, {
            field: "sgValue", name: "Subgroup", size: 'col-xs-2',
            template: function (e) {
                if (hasValue(e) && hasValue(e.sgCode)) {
                    const style = e.isEnabled === false ? 'opacity: 0.5;' : '';
                    return `<span style="${style}">${e.sgValue}</span>`;
                } else {
                    return "Error getting subgroup."
                }
            },
        },
        {
            field: "sop", name: "SOP", size: 'col-xs-1',
            template: function (e) {
                if (hasValue(e) && hasValue(e.sop)) {
                    return `<a onClick='nocManagementAdmin.viewSop(${e.code}); return false;'>View</a>`
                }
            },
        },
    ];
    nocManagementHeaderButtons = [
        { name: "Add Standard", function: "addstandard", src: "/Content/TourImages/add-r.svg" },
        { name: "Add Custom", function: "addcustom", src: "/Content/TourImages/add-r.svg" },
        { name: "Edit SOP", function: "editsop", src: "/Content/TourImages/pen.svg" },
        { name: "Delete", function: "delete", src: "/Content/TourImages/trash.svg" }
    ];
    static readonly OFFICER_STATUS_LEVEL_LABELS: Record<string, string> = {
        DISPATCHED: 'Dispatched',
        ENROUTE: 'Enroute',
        ON_SCENE: 'On Scene',
        CLEARED: 'Cleared',
    };

    customizeSelfInitiateColumns = [
        {
            field: "code", name: "Code", size: 'hidden'
        },
        {
            field: "value", name: "Nature of Call", size: 'col-xs-3',
            template: function (e) {
                if (hasValue(e) && hasValue(e.value)) {
                    return e.value;
                } else {
                    return "Error getting Nature of Call"
                }
            },
        },
        {
            field: "isShownOnMobile", name: "Small Screens?", size: 'col-xs-2',
            template: function (e) {
                if (e.isShownOnMobile) {
                    return 'Yes'
                } else {
                    return '';
                }
            },
        },
        {
            field: "officerStatusLevel", name: "Status", size: 'col-xs-2',
            template: function (e) {
                return NocManagementAdmin.OFFICER_STATUS_LEVEL_LABELS[e.officerStatusLevel] || e.officerStatusLevel || 'On Scene';
            },
        },
        {
            field: "secondaryNocValue", name: "Secondary NOC", size: 'col-xs-2',
            template: function (e) {
                return e.secondaryNocValue || '';
            },
        }
    ];
    customizeSelfInitiateHeaderButtons = [
        { name: "Add", function: "add", src: "/Content/TourImages/add-r.svg" },
        { name: "Edit", function: "edit", src: "/Content/TourImages/pen.svg" },
        { name: "Delete", function: "delete", src: "/Content/TourImages/trash.svg" }
    ];
}


// Window registrations
(window as any).nocManagementAdmin = nocManagementAdmin;
(window as any).ADMIN_NOCManagement = ADMIN_NOCManagement;
(window as any).NocManagementAdmin = NocManagementAdmin;
